import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import {
  buildAttributesFilterTypes,
  CLIENT_ATTRIBUTES,
} from './helper/filterHelper.js';
import countries from 'shared/constants/countries.js';

/**
 * @typedef {Object} FilterOption
 * @property {string|number} id
 * @property {string} name
 * @property {import('vue').VNode} [icon]
 */

/**
 * @typedef {Object} FilterOperator
 * @property {string} value
 * @property {string} label
 * @property {string} icon
 * @property {boolean} hasInput
 */

/**
 * @typedef {Object} FilterType
 * @property {string} attributeKey - The attribute key
 * @property {string} value - This is a proxy for the attribute key used in FilterSelect
 * @property {string} attributeName - The attribute name used to display on the UI
 * @property {string} label - This is a proxy for the attribute name used in FilterSelect
 * @property {'multiSelect'|'searchSelect'|'plainText'|'date'|'booleanSelect'} inputType - The input type for the attribute
 * @property {FilterOption[]} [options] - The options available for the attribute if it is a multiSelect or singleSelect type
 * @property {'text'|'number'} dataType
 * @property {FilterOperator[]} filterOperators - The operators available for the attribute
 * @property {'standard'|'additional'|'customAttributes'} attributeModel
 */

/**
 * @typedef {Object} FilterGroup
 * @property {string} name
 * @property {FilterType[]} attributes
 */

/**
 * Composable that provides conversation filtering context
 * @returns {{ filterTypes: import('vue').ComputedRef<FilterType[]>, filterGroups: import('vue').ComputedRef<FilterGroup[]> }}
 */
export function useClientFilterContext() {
  const { t } = useI18n();

  const clientAttributes = useMapGetter('attributes/getClientAttributes');
  const labels = useMapGetter('labels/getLabels');

  const {
    equalityOperators,
    containmentOperators,
    dateOperators,
    getOperatorTypes,
  } = useOperators();

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const customFilterTypes = computed(() =>
    buildAttributesFilterTypes(
      clientAttributes.value,
      getOperatorTypes,
      'client'
    )
  );

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const filterTypes = computed(() => [
    {
      attributeKey: CLIENT_ATTRIBUTES.NAME,
      value: CLIENT_ATTRIBUTES.NAME,
      attributeName: t('CLIENTS_LAYOUT.FILTER.NAME'),
      label: t('CLIENTS_LAYOUT.FILTER.NAME'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.EMAIL,
      value: CLIENT_ATTRIBUTES.EMAIL,
      attributeName: t('CLIENTS_LAYOUT.FILTER.EMAIL'),
      label: t('CLIENTS_LAYOUT.FILTER.EMAIL'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.PHONE_NUMBER,
      value: CLIENT_ATTRIBUTES.PHONE_NUMBER,
      attributeName: t('CLIENTS_LAYOUT.FILTER.PHONE_NUMBER'),
      label: t('CLIENTS_LAYOUT.FILTER.PHONE_NUMBER'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.IDENTIFIER,
      value: CLIENT_ATTRIBUTES.IDENTIFIER,
      attributeName: t('CLIENTS_LAYOUT.FILTER.IDENTIFIER'),
      label: t('CLIENTS_LAYOUT.FILTER.IDENTIFIER'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.COUNTRY_CODE,
      value: CLIENT_ATTRIBUTES.COUNTRY_CODE,
      attributeName: t('FILTER.ATTRIBUTES.COUNTRY_NAME'),
      label: t('FILTER.ATTRIBUTES.COUNTRY_NAME'),
      inputType: 'searchSelect',
      options: countries,
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'additional',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.CITY,
      value: CLIENT_ATTRIBUTES.CITY,
      attributeName: t('CLIENTS_LAYOUT.FILTER.CITY'),
      label: t('CLIENTS_LAYOUT.FILTER.CITY'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.CREATED_AT,
      value: CLIENT_ATTRIBUTES.CREATED_AT,
      attributeName: t('CLIENTS_LAYOUT.FILTER.CREATED_AT'),
      label: t('CLIENTS_LAYOUT.FILTER.CREATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.LAST_ACTIVITY_AT,
      value: CLIENT_ATTRIBUTES.LAST_ACTIVITY_AT,
      attributeName: t('CLIENTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      label: t('CLIENTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.REFERER,
      value: CLIENT_ATTRIBUTES.REFERER,
      attributeName: t('CLIENTS_LAYOUT.FILTER.REFERER_LINK'),
      label: t('CLIENTS_LAYOUT.FILTER.REFERER_LINK'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.BLOCKED,
      value: CLIENT_ATTRIBUTES.BLOCKED,
      attributeName: t('CLIENTS_LAYOUT.FILTER.BLOCKED'),
      label: t('CLIENTS_LAYOUT.FILTER.BLOCKED'),
      inputType: 'searchSelect',
      options: [
        {
          id: 'true',
          name: t('CLIENTS_LAYOUT.FILTER.BLOCKED_TRUE'),
        },
        {
          id: 'false',
          name: t('CLIENTS_LAYOUT.FILTER.BLOCKED_FALSE'),
        },
      ],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CLIENT_ATTRIBUTES.LABELS,
      value: CLIENT_ATTRIBUTES.LABELS,
      attributeName: t('CLIENTS_FILTER.ATTRIBUTES.LABELS'),
      label: t('CLIENTS_FILTER.ATTRIBUTES.LABELS'),
      inputType: 'multiSelect',
      options: labels.value?.map(label => ({
        id: label.title,
        name: label.title,
      })),
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    ...customFilterTypes.value,
  ]);

  return { filterTypes };
}
